import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import type { ChatPresenterComponent } from '@core/tellent-assistant-contracts';
import type { PresentMetricSummaryArgs } from '@core/tellent-assistant-types';
import { toMetricTileView, type TileView, type TrendArrow } from '@core/tellent-assistant-util';
import { HeadingComponent, IconComponent, type IconName } from '@core/user-interface-legacy';

const ARROW_ICONS: Record<TrendArrow, IconName> = {
  up: 'arrow-upward',
  down: 'arrow-upward',
  right: 'arrow-forward',
};

@Component({
  selector: 'rt-assistant-present-metric-summary',
  imports: [HeadingComponent, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (title(); as titleText) {
      <rt-heading class="title" variant="heading semi-bold">{{ titleText }}</rt-heading>
    }

    <div class="tiles">
      @for (tile of tiles(); track $index) {
        <div class="tile">
          <span class="tile-label">{{ tile.label }}</span>
          <span class="tile-value">{{ tile.value }}</span>

          @if (tile.trend; as trend) {
            <span class="tile-footnote" [attr.data-sentiment]="trend.sentiment">
              @if (trend.arrow; as arrow) {
                <rt-icon
                  class="trend-icon"
                  [name]="arrowIcon(arrow)"
                  [class.flipped]="arrow === 'down'"
                  size="small-xx"
                />
              }
              <span class="tile-footnote-text">
                <span class="tile-change">{{ trend.change }}</span
                >&ngsp;{{ trend.period }}
              </span>
            </span>
          } @else if (tile.note; as noteText) {
            <span class="tile-footnote">
              <span class="tile-footnote-text">{{ noteText }}</span>
            </span>
          }
        </div>
      }
    </div>
  `,
  styleUrl: './present-metric-summary.component.less',
})
export class AssistantPresentMetricSummaryComponent implements ChatPresenterComponent<PresentMetricSummaryArgs> {
  protected readonly title = computed(() => this.args().title);
  protected readonly tiles = computed<readonly TileView[]>(() =>
    (this.args().tiles ?? []).map(toMetricTileView),
  );

  public readonly args = input.required<PresentMetricSummaryArgs>();

  protected arrowIcon(arrow: TrendArrow): IconName {
    return ARROW_ICONS[arrow];
  }
}
